import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { HUB_URL } from './client';

export type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

export interface MessagePostedNudge {
  conversationId: string;
  /** Which unit the thread is in, so a list can tell whether it concerns the one on screen. */
  unitId: string;
  seq: number;
}

/**
 * SignalR is a nudge channel, not a transport of record.
 *
 * Nothing here is treated as durable. The payload carries only a cursor, never message content, so
 * a dropped frame costs nothing — the client reaches identical state through `?after=`. If this
 * connection never establishes at all, the app must still be correct.
 */
export function createHubConnection(): HubConnection {
  return new HubConnectionBuilder()
    .withUrl(HUB_URL, {
      // Railway has no sticky sessions and distributes traffic randomly, so a negotiate request
      // and the follow-up connect can land on different replicas. Skipping negotiation forces a
      // single WebSocket upgrade and sidesteps that entirely.
      //
      // The cost is losing SSE and long-polling fallback, which is not a real loss: long polling
      // is already broken without session affinity.
      skipNegotiation: true,
      transport: HttpTransportType.WebSockets,
      withCredentials: true,
    })
    // Every deploy drops all connections — single replica means no zero-downtime deploys — so
    // reconnect is a routine, load-bearing path rather than an edge case.
    .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
    .configureLogging(import.meta.env.DEV ? LogLevel.Information : LogLevel.Warning)
    .build();
}

export function isConnected(connection: HubConnection | null): boolean {
  return connection?.state === HubConnectionState.Connected;
}
